import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const Footer: React.FC = () => {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const marketCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const marketAnimationFrameRef = useRef<number | null>(null);

  // State
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalPhase, setPortalPhase] = useState<'opening' | 'open' | 'closing'>('opening');
  const [activeDashboard, setActiveDashboard] = useState<'market' | 'crypto' | 'economic'>('market');

  // Helper Functions
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClosePortal = () => {
    setPortalPhase('closing');
  };

  const handleOpenPortal = (e: React.MouseEvent) => {
    e.preventDefault();
    setPortalOpen(true);
    setPortalPhase('opening');
  };

  // Portal Animation Effect
  useEffect(() => {
    if (!portalOpen || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particleCount = 300;
    const particles: { x: number; y: number; size: number; speed: number; angle: number; color: string }[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speed: Math.random() * 3 + 0.5,
        angle: Math.random() * Math.PI * 2,
        color: `hsl(${Math.random() * 30 + 20}, 100%, ${Math.random() * 20 + 70}%)`,
      });
    }

    let portalRadius = 0;
    const maxRadius = Math.max(canvas.width, canvas.height) * 0.6;
    const startTime = Date.now();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const elapsedTime = Date.now() - startTime;

      if (portalPhase === 'opening') {
        portalRadius = Math.min(maxRadius, (elapsedTime / 1000) * maxRadius * 0.7);
        if (portalRadius >= maxRadius) setPortalPhase('open');
      } else if (portalPhase === 'closing') {
        portalRadius = Math.max(0, maxRadius - (elapsedTime / 800) * maxRadius);
        if (portalRadius <= 0) {
          setPortalOpen(false);
          return;
        }
      }

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, portalRadius);
      gradient.addColorStop(0, 'rgba(255, 140, 0, 0.8)');
      gradient.addColorStop(0.6, 'rgba(255, 100, 0, 0.4)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, portalRadius, 0, Math.PI * 2);
      ctx.fill();

      particles.forEach((p) => {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        const distToCenter = Math.hypot(p.x - centerX, p.y - centerY);
        if (distToCenter < portalRadius * 1.5) {
          const angleToCenter = Math.atan2(centerY - p.y, centerX - p.x);
          if (portalPhase === 'opening') {
            p.angle = angleToCenter;
            p.speed = Math.min(10, p.speed * 1.02);
          } else if (portalPhase === 'closing') {
            p.angle = angleToCenter + Math.PI;
            p.speed = Math.min(12, p.speed * 1.03);
          } else {
            p.angle = angleToCenter + Math.PI / 2;
            p.speed = 2 + (portalRadius - distToCenter) / portalRadius * 3;
          }
        }

        p.x += Math.cos(p.angle) * p.speed;
        p.y += Math.sin(p.angle) * p.speed;

        if (p.x < -50 || p.x > canvas.width + 50 || p.y < -50 || p.y > canvas.height + 50) {
          p.x = centerX + (Math.random() - 0.5) * 100;
          p.y = centerY + (Math.random() - 0.5) * 100;
          p.size = Math.random() * 3 + 1;
          p.speed = Math.random() * 3 + 0.5;
          p.color = `hsl(${Math.random() * 40 + 15}, 100%, ${Math.random() * 20 + 70}%)`;
        }
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [portalOpen, portalPhase]);

  // Market Trends Animation Effect
  useEffect(() => {
    if (activeDashboard !== 'market' || !marketCanvasRef.current) return;
    const canvas = marketCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.parentElement?.clientWidth || 300;
    canvas.height = 300;

    // Placeholder data for S&P 500, Nasdaq, Dow Jones (normalized values)
    const marketData = {
      'S&P 500': [100, 102, 101, 103, 105, 104, 106, 107, 108, 110],
      'Nasdaq': [95, 96, 98, 99, 101, 103, 102, 104, 106, 108],
      'Dow Jones': [98, 97, 99, 100, 102, 101, 103, 104, 105, 106],
    };

    const colors = {
      'S&P 500': '#FF6E00',
      'Nasdaq': '#00C4B4',
      'Dow Jones': '#FFD700',
    };

    let frame = 0;
    const animateMarket = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw grid lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = (canvas.height / 5) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Animate each index
      Object.entries(marketData).forEach(([name, data], index) => {
        ctx.strokeStyle = colors[name as keyof typeof colors];
        ctx.fillStyle = colors[name as keyof typeof colors];
        ctx.lineWidth = 2;

        const xStep = canvas.width / (data.length - 1);
        const yScale = canvas.height / 20; // Scale data to fit canvas height

        ctx.beginPath();
        data.forEach((value, i) => {
          const x = i * xStep;
          const y = canvas.height - (value - 90) * yScale + Math.sin(frame / 20 + i) * 5;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.stroke();

        // Draw points
        data.forEach((value, i) => {
          const x = i * xStep;
          const y = canvas.height - (value - 90) * yScale + Math.sin(frame / 20 + i) * 5;
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fill();
        });

        // Label
        ctx.font = '12px Roboto';
        ctx.fillText(name, 10, 20 + index * 20);
      });

      frame++;
      marketAnimationFrameRef.current = requestAnimationFrame(animateMarket);
    };

    marketAnimationFrameRef.current = requestAnimationFrame(animateMarket);
    return () => {
      if (marketAnimationFrameRef.current !== null) {
        cancelAnimationFrame(marketAnimationFrameRef.current);
      }
    };
  }, [activeDashboard]);

  // Render
  return (
    <footer className="footer">
      {/* Footer Top */}
      <div className="footer-top">
        <div className="footer-container">
          <div className="footer-columns">
            <div className="footer-column">
              <div className="footer-logo">
                <img src="/logo512.png" alt="Adelphi Finance Club Logo" className="footer-club-logo" />
                <h2>Adelphi University Finance Club</h2>
              </div>
              <p className="footer-tagline">Preparing Future Leaders in Finance at Adelphi University</p>
              <div className="social-links">
                <a 
                  href="https://www.linkedin.com/groups/13180753/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="LinkedIn" 
                  className="social-icon"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <span style={{ color: 'white', fontWeight: 'bold' }}>in</span>
                </a>
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-icon instagram"></a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="social-icon twitter"></a>
              </div>
            </div>
            <div className="footer-column">
              <h3>Quick Links</h3>
              <ul className="footer-links">
                <li><Link to="/" onClick={handleLinkClick}>Home</Link></li>
                <li><Link to="/events" onClick={handleLinkClick}>Events</Link></li>
                <li><Link to="/news" onClick={handleLinkClick}>News</Link></li>
                <li><Link to="/members" onClick={handleLinkClick}>Members</Link></li>
                <li><Link to="/contact" onClick={handleLinkClick}>Contact</Link></li>
              </ul>
            </div>
            <div className="footer-column">
              <h3>Resources</h3>
              <ul className="footer-links">
                <li>
                  <a href="https://business.adelphi.edu/" target="_blank" rel="noopener noreferrer">
                    Willumstad School of Business
                  </a>
                </li>
                <li>
                  <a href="https://myaulife.adelphi.edu/organization/financeclub/" target="_blank" rel="noopener noreferrer">
                    AULife Finance Club
                  </a>
                </li>
                <li>
                  <a href="https://libraries.adelphi.edu/business/" target="_blank" rel="noopener noreferrer">
                    Business Library Resources
                  </a>
                </li>
                <li>
                  <a href="https://portal.adelphi.edu/services/redirect.php?service_id=122" target="_blank" rel="noopener noreferrer">
                    Member Services
                  </a>
                </li>
                {/* Moved LinkedIn link */}
                <li>
                  <a href="https://www.linkedin.com/groups/13180753/" target="_blank" rel="noopener noreferrer">
                    LinkedIn Group
                  </a>
                </li>
              </ul>
            </div>
            <div className="footer-column">
              <h3>Contact</h3>
              <ul className="contact-info">
                <li>
                  <span className="contact-icon location"></span>
                  <span>
                    Adelphi University<br />
                    1 South Ave, Garden City, NY 11530
                  </span>
                </li>
                <li>
                  <span className="contact-icon email"></span>
                  <a href="mailto:financeclub@adelphi.edu">financeclub@adelphi.edu</a>
                </li>
                <li>
                  <span className="contact-icon meeting"></span>
                  <span>
                    Meetings: Wednesdays at 5:30 PM<br />
                    Business Building, Room 215
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-container">
          <div className="copyright-section">
            <p>© {new Date().getFullYear()} Adelphi University Finance Club. All rights reserved.</p>
            <p className="footer-disclaimer">This is a student-run organization at Adelphi University.</p>
          </div>
        </div>
      </div>

      {/* Gaazzeebo Section */}
      <div className="gaazzeebo-section">
        <div className="gaazzeebo-container">
          <div className="gaazzeebo-wrapper">
            <a
              href="https://gaazzeebo.net"
              target="_blank"
              rel="noopener noreferrer"
              className="gaazzeebo-link"
              onContextMenu={(e) => e.preventDefault()}
            >
              Powered by <span className="gaazzeebo-text"> Gaazzeebo</span>
              <span className="gaazzeebo-icon">↗</span>
            </a>
            <button 
              className="dashboard-button" 
              onClick={handleOpenPortal}
              aria-label="Open Finance Dashboard"
            >
              <span className="dashboard-icon">📊</span>
              <span className="dashboard-label">Finance Dashboard</span>
            </button>
          </div>
        </div>
      </div>

      {/* Finance Portal */}
      {portalOpen && (
        <div className="finance-portal">
          <canvas ref={canvasRef} className="portal-canvas"></canvas>
          <div className={`portal-content ${portalPhase === 'open' ? 'visible' : ''}`}>
            <div className="portal-header">
              <div className="portal-logo">
                <div className="portal-logo-icon">G</div>
                <h2>Gaazzeebo Finance Portal</h2>
              </div>
              <div className="portal-nav">
                <button className={activeDashboard === 'market' ? 'active' : ''} onClick={() => setActiveDashboard('market')}>
                  <span className="nav-icon market-icon"></span>Market Trends
                </button>
                <button className={activeDashboard === 'crypto' ? 'active' : ''} onClick={() => setActiveDashboard('crypto')}>
                  <span className="nav-icon portfolio-icon"></span>Cryptocurrency
                </button>
                <button className={activeDashboard === 'economic' ? 'active' : ''} onClick={() => setActiveDashboard('economic')}>
                  <span className="nav-icon news-icon"></span>Economic Calendar
                </button>
              </div>
              <button className="portal-close" onClick={handleClosePortal}>
                <span className="close-icon"></span>
              </button>
            </div>
            <div className="portal-dashboard">
              {activeDashboard === 'market' && (
                <div className="market-dashboard">
                  <div className="dashboard-section">
                    <h3 className="section-title">Market Trends Visualization</h3>
                    <canvas ref={marketCanvasRef} className="market-canvas"></canvas>
                    <p className="market-note">Dynamic visualization of S&P 500, Nasdaq, and Dow Jones trends.</p>
                  </div>
                </div>
              )}
              {activeDashboard === 'crypto' && (
                <div className="crypto-dashboard">
                  <div className="dashboard-section">
                    <h3 className="section-title">Cryptocurrency Prices</h3>
                    <div style={{ height: '400px', width: '100%' }}>
                      <iframe
                        src="https://www.tradingview.com/widgetembed/?hideideas=1&theme=dark&style=1&interval=D&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=[]&hidevolume=1&symbol=CRYPTO%3ABTCUSD&symbol=CRYPTO%3AETHUSD&symbol=CRYPTO%3ASOLUSD"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Crypto Prices Widget"
                      ></iframe>
                    </div>
                  </div>
                </div>
              )}
              {activeDashboard === 'economic' && (
                <div className="economic-dashboard">
                  <div className="dashboard-section">
                    <h3 className="section-title">Economic Calendar</h3>
                    <div style={{ height: '400px', width: '100%' }}>
                      <iframe
                        src="https://www.cashbackforex.com/widgets/economic-calendar?timeframe=week"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Economic Calendar Widget"
                      ></iframe>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
